import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import EditIcon from "../../../src/assets/icons/EditIcon.svg";
import {
  Box,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../../components/ui/Pagination";
import SortableTable, {
  SortableColumn,
} from "../../components/common/SortableTable";
import CreateRentalPage from "./Create-Plan/CreatePlanPage";
import apiService from "../../services/api";
import { DATE_FORMATS } from "../../constants";
import useCodeCommon from "../../hooks/useCodeCommon";
import { SearchableSelectInput } from "../../components/forms";
import CustomLoading from "../../components/common/CustomLoading";
import useRoleBasedFiltering from "../../hooks/useRoleBasedFiltering";

interface PlanData {
  id: string;
  city: string;
  hubName: string;
  status: string;
  hubmasterid?: string | number;
}

const StatusChip = ({ value }: { value: string }) => {
  const getChipStyles = (status: string) => {
    switch (status) {
      case "Active":
        return {
          backgroundColor: "rgba(212, 255, 199, 1)",
          color: "rgba(31, 113, 13, 1)",
          fontWeight: 600,
        };
      case "Inactive":
        return {
          backgroundColor: "rgba(255, 227, 227, 1)",
          color: "rgba(197, 0, 0, 1)",
          fontWeight: 600,
        };
      case "No Plans":
        return {
          backgroundColor: "rgba(255, 248, 220, 1)",
          color: "rgba(184, 134, 11, 1)",
          fontWeight: 600,
        };
      default:
        return {
          backgroundColor: "rgba(245, 245, 245, 1)",
          color: "rgba(117, 117, 117, 1)",
          fontWeight: 600,
        };
    }
  };

  return <Chip label={value} size="small" sx={getChipStyles(value)} />;
};

const PlanManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialFilters = (location.state as { filters?: any })?.filters || {
    city: "",
    status: "",
  };
  const initialPage = (location.state as { page?: number })?.page || 0;

  const [formData, setFormData] = useState(initialFilters);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [citiesMaster, setCitiesMaster] = useState<
    Array<{ city_id: number; city_name: string; state_id: number }>
  >([]);
  const [rawPlans, setRawPlans] = useState<any[]>([]);
  const { filterByRole } = useRoleBasedFiltering<PlanData>();
  const {
    options: planStatusOptions,
    loading: planStatusLoading,
    error: planStatusError,
  } = useCodeCommon("PlanStatus");

  const getStatusDisplayName = (planstatusid: number): string => {
    const statusOption = planStatusOptions.find(
      (option) => parseInt(option.codevalue) === planstatusid
    );
    return statusOption ? statusOption.codedisplayname : "Unknown";
  };

  const fetchMasterCities = async () => {
    try {
      const response = await apiService.getCities();
      const data = (response as any)?.data || response;
      let list: any[] = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray((data as any).result))
        list = (data as any).result;
      else if (data && Array.isArray((data as any).cities))
        list = (data as any).cities;
      setCitiesMaster(list);
    } catch (e) {
      setCitiesMaster([]);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
  
      const response: any = await apiService.getAllPlans();
  
      const plansData: any[] =
        Array.isArray(response)
          ? response
          : response?.data ?? [];
  
      if (!plansData.length) {
        setError("No plans available");
        setLoading(false);
        return;
      }
  
      setRawPlans(plansData);
      // Don't set loading to false here - let the useEffect handle it after data processing
    } catch (err) {
      setError("Error loading plans. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterCities();
    fetchPlans();
  }, []);

  useEffect(() => {
    const state = location.state as any;
    if (state?.refresh || state?.refreshHubs) {
      fetchPlans();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    if (rawPlans.length > 0 && citiesMaster.length > 0 && planStatusOptions.length > 0) {
      const mapped = rawPlans.map((plan: any) => {

        const statusString = getStatusDisplayName(plan.planstatusid || 0);
        const planCityId =
          parseInt(plan.city?.toString() || plan.cityId?.toString() || "0") ||
          0;
        const planStateId =
          parseInt(plan.state?.toString() || plan.stateId?.toString() || "0") ||
          0;
        const matchedCity = citiesMaster.find(
          (c) =>
            Number(c.city_id) === planCityId &&
            Number(c.state_id) === planStateId
        );
        const cityDisplay = matchedCity?.city_name || "N/A";

        return {
          id: String(plan.hubmasterid || ""),
          city: cityDisplay,
          hubName: plan.hubname || "N/A",
          status: statusString,
          hubmasterid: plan.hubmasterid || plan.hubid,
        };
      });
      const roleFilteredData = filterByRole(mapped, 'hubmasterid');
      setPlans(roleFilteredData);
      setLoading(false); // Set loading to false only after data is fully processed
    }
  }, [citiesMaster, rawPlans, planStatusOptions]);

  const filteredPlans = plans.filter((plan) => {
    const matchesCity =
      !formData.city ||
      plan.city.toLowerCase().includes(formData.city.toLowerCase());
    const matchesStatus = !formData.status || plan.status === formData.status;
    return matchesCity && matchesStatus;
  });

  const pagination = usePagination({
    totalItems: filteredPlans.length,
    initialPage: initialPage,
    initialRowsPerPage: 25,
  });
  const paginatedPlans = pagination.paginatedData(filteredPlans);

  const handleFormDataChange = (field: string, value: string) => {
    const newFilters = { ...formData, [field]: value };
    setFormData(newFilters);
    pagination.goToPage(0);

    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        filters: newFilters,
        page: 0,
      },
    });
  };

  const handleEditPlan = (plan: PlanData) => {
    const hubmasterid = plan.id;
    if (!hubmasterid) return;
    navigate(`/plan-management/edit-plan/${hubmasterid}`, {
      state: {
        filters: formData,
        page: pagination.page,
      },
    });
  };

  const columns: SortableColumn<PlanData>[] = [
    { id: "hubName", label: "Hub Name" },
    { id: "city", label: "City" },
    {
      id: "status",
      label: "Status",
      align: "center",
      render: (row) => <StatusChip value={row.status} />,
    },
    {
      id: "actions" as keyof PlanData,
      label: "Action",
      align: "center",
      sortable: false,
      render: (row: PlanData) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
          <Button
            variant="text"
            sx={{ p: 0, minWidth: "auto" }}
            onClick={() => handleEditPlan(row)}
          >
            <img src={EditIcon} alt="Edit" width={30} height={30} />
          </Button>
        </Box>
      ),
    },
  ];

  if (location.pathname === "/plan-management/create-plan") {
    return <CreateRentalPage onBack={() => window.history.back()} />;
  }

  return (
    <>
      <Container fluid className="px-0">
        {(error || planStatusError) && (
          <Alert
            severity="error"
            className="mb-3"
            onClose={() => setError(null)}
          >
            {error || planStatusError}
          </Alert>
        )}

        <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <SearchableSelectInput
                field="city"
                formData={formData}
                onChange={handleFormDataChange}
                label="City"
                placeholder="Select city"
                options={citiesMaster.map((city) => ({
                  label: city.city_name,
                  value: city.city_name,
                }))}
                loading={citiesMaster.length === 0}
              />
            </Col>

            <Col xs={12} md={3}>
              <SearchableSelectInput
                field="status"
                formData={formData}
                onChange={handleFormDataChange}
                label="Status"
                placeholder="Select status"
                options={planStatusOptions.map((statusOption) => ({
                  label: statusOption.codedisplayname,
                  value: statusOption.codedisplayname,
                }))}
                loading={planStatusLoading}
              />
            </Col>

            <Col xs={12} md={6} className="d-flex gap-2 justify-content-end" />
          </Row>
        </Paper>

        <Paper className="border-0 rounded-0 shadow-none">
          {loading || planStatusLoading ? (
            <CustomLoading message="Fetching Plans Table Data..." />
          ) : (
            <SortableTable
              data={paginatedPlans}
              columns={columns}
              emptyMessage="No plans found"
            />
          )}
        </Paper>
      </Container>

      <Pagination
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        totalItems={filteredPlans.length}
        totalPages={pagination.totalPages}
        startItem={pagination.startItem}
        endItem={pagination.endItem}
        rowsPerPageOptions={[5, 10, 25, 50]}
        showRowsPerPage
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        onGoToNextPage={pagination.goToNextPage}
        onGoToPreviousPage={pagination.goToPreviousPage}
      />

    </>
  );
};

export default PlanManagementPage;
